import AppSettingsService from "./AppSettingsService";
import {
  AccountInfo,
  Configuration,
  AuthenticationResult,
  PublicClientApplication,
  SilentRequest,
  RedirectRequest,
  EndSessionRequest,
  LogLevel,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { noop, User } from "@amsanalytics/react-shared-library";
export interface AuthProps {
  authService: IAuthService;
}
export interface IAuthService {
  SignIn: () => void;
  SignOut: () => void;
  getToken: () => Promise<string>;
  // getToken2: () => Promise<string>; // TODO: Replace all calls to getToken with this one
  handlePageLoadEvent: () => Promise<void>;
}
class AuthService implements IAuthService {
  constructor(appSettings: AppSettingsService) {
    if (!appSettings) {
      throw new Error("the app settings service was not provided");
    }

    this.appSettings = appSettings;

    const msalConfig = this.GetMsalClientConfiguration();
    this.msalApplication = new PublicClientApplication(msalConfig);
  }

  userSignedIn: (user: Partial<User>) => void = noop;
  userSignedOut: () => void = noop;

  // msal application object
  msalApplication: PublicClientApplication;

  // settings service
  appSettings: AppSettingsService;

  // cached account info
  account: AccountInfo | null = null;

  /**
   * Clears all MSAL accounts and cache, then redirects to sign-in.
   * Use this when refresh tokens are expired and a fresh login is required.
   */
  clearCacheAndSignIn = () => {
    console.log('Clearing MSAL cache due to expired tokens and redirecting to sign-in...');
    
    // Clear the cached account
    this.account = null;
    
    // Clear MSAL keys from localStorage (the cache location)
    const clientId = this.appSettings.GetMsalClientId();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('msal.') || 
        key.includes('login.windows') ||
        key.includes(clientId)
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Redirect to sign-in
    this.SignIn();
  };

  getRedirectRequest = (): RedirectRequest => {
    const redirectRequest: RedirectRequest = {
      scopes: [this.appSettings.GetMsalClientScope()],      
      prompt: "select_account",
    };
    return redirectRequest;
  }


  isAuthenticated = (): boolean => {
    return this.account !== undefined && this.account !== null;
  };
  handlePageLoadEvent = (): Promise<void> => {
    // Handle redirect promise with error handling for expired tokens
    return this.msalApplication.handleRedirectPromise()
      .then((authResult: AuthenticationResult | null) => {
        this.handleRedirectResponse(authResult);
      })
      .catch((error) => {
        // Check for expired refresh token (invalid_grant / bad_token / AADSTS700084)
        const errorMessage = error?.message || error?.errorMessage || '';
        const errorCode = error?.errorCode || '';
        const errorDesc = error?.errorDescription || '';
        
        if (errorMessage.includes('invalid_grant') || 
            errorMessage.includes('AADSTS700084') || 
            errorCode === 'invalid_grant' ||
            errorMessage.includes('bad_token') ||
            errorDesc.includes('invalid_grant') ||
            errorDesc.includes('AADSTS700084')) {
          console.log('Refresh token expired, clearing cache and redirecting to sign-in...');
          this.clearCacheAndSignIn();
          return;
        }
        // Re-throw other errors
        throw error;
      });
  };

  handleRedirectResponse = async (authResult: AuthenticationResult | null) => {
    // if this page load is redirect from the Microsoft Identity platform then the
    // authResult will be populated. Otherwise null on other page loads.

    if (authResult !== null) {
      // save the fresh account info from the result.
      this.account = authResult.account;
    } else {
      // see if we have cached accounts.
      const currentAccounts = this.msalApplication.getAllAccounts();

      if (currentAccounts === null || currentAccounts.length < 1) {
        // no cached accounts.
        // user will need to click the sign-in button and redirect to login.
        //return;
        this.SignIn();
        return;

      } else if (currentAccounts.length > 1) {
        // there are some situations where the user may have multiple (different) cached logins.
        // this conditional block would need to be updated to support multiple accounts.
        // otherwise it will just grab the first one below.
        // TODO: What do we want to do in this case?
        console.warn("Multiple accounts detected in MSAL account cache.");

        let accountIndex = 0;
        // TODO: In this case we need to have a mechanism by which the user can select which account to use

        // if(currentAccounts.length > 1) {
        //   const account = window.prompt(`We found multiple accounts that you are logged into. Enter the number of the account to use (1-${currentAccounts.length}).`, "1");
        //   accountIndex = parseInt(account || "1") - 1;
        // }
        this.account = currentAccounts[accountIndex];
      } else if (currentAccounts.length === 1) {
        // we have exactly 1 cached account.
        // set the account info. user may not need to sign in.        
        this.account = currentAccounts[0];
        const token = await this.getToken();
        // Check to see if expired. If so go to signin immediately
        // console.log(token);      
      }
    }
    if (this.account) {
      this.userSignedIn({
        // userName: this.account.username,
        adUserId: this.account.username,
        firstName: this.account.name || "",
        lastName: "",
      });
    }
  };

  GetMsalClientConfiguration = (): Configuration => {
    return {
      auth: {
        clientId: this.appSettings.GetMsalClientId(),
        authority: this.appSettings.GetMsalTenantAuthorityUri(),
        redirectUri: this.appSettings.GetLoginRedirectUri(),
      },
      cache: {
        cacheLocation: this.appSettings.GetMsalCacheLocation(),
        storeAuthStateInCookie: this.appSettings.GetMsalStoreAuthInCookie(),
      },
      system: {
        loadFrameTimeout: 30000,
        tokenRenewalOffsetSeconds: 60,
        loggerOptions: {
          loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
            // console.log("Login Info", {
            //   level,
            //   message,
            //   containsPii,
            // });
          },
          piiLoggingEnabled: false,
          logLevel: LogLevel.Verbose,
        },
      },
    };
  };

  getToken = async (): Promise<string> => {
    const tokenRequest: SilentRequest = {
      account: this.account
        ? this.account as AccountInfo
        : this.msalApplication.getAllAccounts() && this.msalApplication.getAllAccounts().length > 0
          ? this.msalApplication.getAllAccounts()[0]
          : {} as AccountInfo, // This will and should fail 
      // scopes: [this.appSettings.GetMsalClientScope(), "User.Read"],
      scopes: [this.appSettings.GetMsalClientScope()]
      // scopes: ['bbe4b585-2666-4406-8683-357215458fee']
    };

    // msal will return the cached token if present, or call to get a new one
    // if it is expired or near expiring.
    const tokenResponse = await this.msalApplication.acquireTokenSilent(tokenRequest)
      .catch(error => {
        if (error instanceof InteractionRequiredAuthError) {
          // console.log('acquiring new token');
          return this.msalApplication.acquireTokenRedirect(this.getRedirectRequest());
        } else {
          // Check for expired refresh token (invalid_grant / bad_token)
          const errorMessage = error?.message || error?.errorMessage || '';
          const errorCode = error?.errorCode || '';
          if (errorMessage.includes('invalid_grant') || 
              errorMessage.includes('AADSTS700084') || 
              errorCode === 'invalid_grant' ||
              errorMessage.includes('bad_token')) {
            console.log('Refresh token expired, clearing cache and redirecting to sign-in...');
            this.clearCacheAndSignIn();
            return;
          }
          console.log("Token Error: ", error);          
        }
      });

    if ((tokenResponse as AuthenticationResult)?.accessToken) {
      return Promise.resolve((tokenResponse as AuthenticationResult).accessToken);
    } else {
      // NOTE: If this is the case the user should already be redirected to the login screen or getting a refresh token
      return Promise.resolve("error");
    }
  };



  SignIn = () => {


    // this will redirect the web application to the Microsoft Identity platform sign in pages.
    // no code will execute after this point.
    this.msalApplication.loginRedirect(this.getRedirectRequest());
  };

  SignOut = () => {
    if (!this.account) {
      // no cached login to signout
      return;
    }

    const accountInfo: AccountInfo | null = this.msalApplication.getAccountByUsername(this.account?.username as string);

    if (accountInfo !== null) {
      const logoutRequestPayload: EndSessionRequest = {
        account: accountInfo,
      };

      this.msalApplication.logout(logoutRequestPayload);
    }
    this.userSignedOut();
  };
}

export default AuthService;


export const MockAuthService: IAuthService = {
  SignIn: noop,
  SignOut: noop,
  getToken: () => Promise.resolve(""),
  // getToken2: () => Promise.resolve(''),
  handlePageLoadEvent: () => Promise.resolve(),
}